/*
 * MappingInputs.tsx
 * Author: fwunderlich
 * Date: 24.11.2023
 *
 * Copyright: DMT GmbH & Co. KG
 */

import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import Input from '../global/Input';
import {classNames} from 'primereact/utils';
import {useAppSelector} from '../../redux/hooks';
import {Mapping, MappingType} from '../../redux/slices/MappingSlice';
import {useGetPersonenTypesQuery} from '../../redux/rtk/injectedMappingApi';
import {useGetPersonQuery} from '../../redux/rtk/injectedPersonApi';
import {getPersonLabel, Person} from '../functions/GlobalData';
import {MappingPersons} from './MappingPersons';
import {useGetProjectCorrespondenceTypesQuery} from '../../redux/rtk/injectedCorrespondenceApi';
import {useGetProjectContractTypesQuery} from '../../redux/rtk/injectedContractApi';

type Props = {
    id: string,
    projectId: string,
    mappingType?: MappingType,
    onChange: (mapping: any) => void,
    mappings: any,
    // map_person_type_id: number,
    // map_anteileigentuemer: string,
    // map_miteigentumsanteil: string,
    // map_artrechtsgemeinschaft: string,
    // map_bvnr: number,
    // map_gr_ha: number,
    // map_gr_a: number,
    // map_gr_m2: number,
    name?: string,
    className?: string,
    overlay?: boolean,
    setValidationError?: (hasError: boolean) => void,
    activeMappings: any[],
    autoCompleteComponent: any,
    edit?: boolean
}

export const MappingInputs = (props: Props): JSX.Element => {
    const {t} = useTranslation(['common', 'globalData']);

    const mappings: Mapping[] = useAppSelector((state: any) => state.mappingData.mappings);

    // const [anteileigentuemer, setAnteileigentuemer] = useState<string | undefined>(props.anteileigentuemer);
    // const [miteigentumsanteil, setMiteigentumsanteil] = useState<string | undefined>(props.miteigentumsanteil);
    // const [artrechtsgemeinschaft, setArtrechtsgemeinschaft] = useState<string | undefined>(props.artrechtsgemeinschaft);
    // const [personType, setPersonType] = useState<number>(props.person_type);
    const [personTypes, setPersonTypes] = useState<any>([]);
    const [correspondenceTypes, setCorrespondenceTypes] = useState<any>([]);
    const [contractTypes, setContractTypes] = useState<any>([]);

    const [mapping, setMapping] = useState<any>({});
    const [showAnteilEigentuemerError, setShowAnteilEigentuemerError] = useState<boolean>(false);
    const [skipPerson, setSkipPerson] = useState<boolean>(true);
    const [skipPersonTypes, setSkipPersonTypes] = useState<boolean>(true);
    const [skipCorrespondenceTypes, setSkipCorrespondenceTypes] = useState<boolean>(true);
    const [skipContractTypes, setSkipContractTypes] = useState<boolean>(true);


    const personTypesQuery = useGetPersonenTypesQuery({mappingType: props.mappingType}, {skip: skipPersonTypes});

    const persons = useGetPersonQuery({
        // projectId: props.projectId,
        projectId: props.projectId,
        personId: '',
        commonData: true,
        mapping: {mapping: true}
    }, {skipPerson});

    const correspondenceTypesQuery = useGetProjectCorrespondenceTypesQuery({
        projectId: props.projectId,
        personId: props.id
    }, {skip: skipCorrespondenceTypes});

    const contractTypesQuery = useGetProjectContractTypesQuery({
        projectId: props.projectId,
        flurstueckId: props.id
    }, {skip: skipContractTypes});

    useEffect(() => {
        switch (props.mappingType) {
            case MappingType.personCorrespondence:
                setSkipCorrespondenceTypes(false);
                break;

            case MappingType.parcelContract:
                setSkipContractTypes(false);
                break;

            default:
                setSkipPersonTypes(false);
                break;
        }
    }, [props.mappingType]);

    useEffect(() => {
        setSkipPerson(mapping.person_type_id === 8);
    }, [mapping.person_type_id]);

    useEffect(() => {
        let item = mappings.find(x => x.id === props.id);
        if (item) {
            let tempItem = item;
            setMapping((prevState: any) => {
                let temp = {...prevState};
                temp.person_type_id = tempItem.person_type_id;
                temp.anteileigentuemer = tempItem.anteileigentuemer;
                temp.miteigentumsanteil = tempItem.miteigentumsanteil;
                temp.artrechtsgemeinschaft = tempItem.artrechtsgemeinschaft;

                temp.bvnr = tempItem.bvnr;
                temp.gr_ha = tempItem.gr_ha;
                temp.gr_a = tempItem.gr_a;
                temp.gr_m2 = tempItem.gr_m2;

                temp.lfdnr_eintragung = tempItem.lfdnr_eintragung;
                temp.lfdnr_bestandsverzeichnis = tempItem.lfdnr_bestandsverzeichnis;
                temp.grundlage_eintragung = tempItem.grundlage_eintragung;
                temp.ehepartner = tempItem.ehepartner;
                temp.erbengemeinschaft = tempItem.erbengemeinschaft;

                temp.korrespondenz_type_id = tempItem.korrespondenz_type_id;
                temp.datum_soll = tempItem.datum_soll ? new Date(tempItem.datum_soll) : undefined;
                temp.datum_ist = tempItem.datum_ist ? new Date(tempItem.datum_ist) : undefined;

                temp.flaeche_erwerb = tempItem.flaeche_erwerb;
                temp.flaeche_dinglich = tempItem.flaeche_dinglich;
                temp.flaeche_temp = tempItem.flaeche_temp;

                temp.vertrag_type_id = tempItem.vertrag_type_id;

                if (temp.gr_ha) {
                    onChange('gr_ha', temp.gr_ha);
                }
                if (temp.gr_a) {
                    onChange('gr_a', temp.gr_a);
                }
                if (temp.gr_m2) {
                    onChange('gr_m2', temp.gr_m2);
                }

                return temp;
            });
        } else {
            setMapping((prevState: any) => {
                let temp = {...prevState};

                for (let i = 0; i < Object.keys(props.mappings).length; i++) {
                    let map_key = Object.keys(props.mappings)[i];
                    if (map_key.indexOf('map_') >= 0) {
                        let key = map_key.replace('map_', '');
                        let value = (props.mappings as any)[map_key];

                        temp[key] = value;
                    }
                }

                // if (temp.person_type_id == null) {
                //     if (props.mappingType === MappingType.personLandRegister) {
                //         temp.person_type_id = 1;
                //     } else {
                //         if (temp.person_type_id == null) {
                //             temp.person_type_id = 0;
                //         }
                //     }
                // }

                if (temp.gr_ha) {
                    onChange('gr_ha', temp.gr_ha);
                }
                if (temp.gr_a) {
                    onChange('gr_a', temp.gr_a);
                }
                if (temp.gr_m2) {
                    onChange('gr_m2', temp.gr_m2);
                }

                return temp;
            });
        }
    }, [JSON.stringify(props.mappings)]);

    useEffect(() => {
        if (personTypesQuery.data) {
            setPersonTypes(personTypesQuery.data.map((x: any) => {
                return {label: x.name, value: x.id};
            }));
        }
    }, [personTypesQuery]);

    useEffect(() => {
        if (correspondenceTypesQuery.data) {
            setCorrespondenceTypes(correspondenceTypesQuery.data.filter((x: any) => {
                return props.activeMappings.indexOf(x.korrespondenz_type_id) < 0;
            }).map((x: any) => {
                return {label: x.korrespondenz_type_name, value: x.korrespondenz_type_id};
            }));
        }
    }, [correspondenceTypesQuery]);

    useEffect(() => {
        if (contractTypesQuery.data) {
            setContractTypes(contractTypesQuery.data.filter((x: any) => {
                return props.activeMappings.indexOf(x.id) < 0;
            }).map((x: any) => {
                return {label: x.name, value: x.id};
            }));
        }
    }, [contractTypesQuery]);

    useEffect(() => {
        const asyncFunc = async () => {
            let eigentuemer = mapping.anteileigentuemer;
            if (eigentuemer != null && eigentuemer != '') {
                setShowAnteilEigentuemerError(eigentuemer.match(/^([1-9]|[1-9][0-9]|[1-9][0-9][0-9]|[1-9][0-9][0-9][0-9])\/([1-9]|[1-9][0-9]|[1-9][0-9][0-9]|[1-9][0-9][0-9][0-9])$/g) == null);
            } else {
                setShowAnteilEigentuemerError(false);
            }
        };
        asyncFunc();
    }, [mapping]);

    useEffect(() => {
        if (personTypes && personTypes.length && mapping && !mapping.person_type_id) {
            setMapping((prevState: any) => {
                let temp = {...prevState};

                temp.person_type_id = personTypes[0].value;
                onChange('person_type_id', personTypes[0].value);
                return temp;
            });
        }
    }, [personTypes, mapping]);

    useEffect(() => {
        if (correspondenceTypes && correspondenceTypes.length && mapping && !mapping.korrespondenz_type_id) {
            setMapping((prevState: any) => {
                let temp = {...prevState};

                temp.korrespondenz_type_id = correspondenceTypes[0].value;
                onChange('korrespondenz_type_id', correspondenceTypes[0].value);
                return temp;
            });
        }
    }, [correspondenceTypes, mapping]);

    useEffect(() => {
        console.log('contractTypes', contractTypes);
        if (contractTypes && contractTypes.length && mapping && !mapping.vertrag_type_id) {
            setMapping((prevState: any) => {
                let temp = {...prevState};

                temp.vertrag_type_id = contractTypes[0].value;
                onChange('vertrag_type_id', contractTypes[0].value);
                return temp;
            });
        }
    }, [contractTypes, mapping]);

    useEffect(() => {
        if (props.setValidationError) {
            props.setValidationError(showAnteilEigentuemerError);
        }
    }, [showAnteilEigentuemerError]);

    // const onChange = (person_type: number, anteileigentuemer: string | undefined, miteigentumsanteil: string | undefined, artrechtsgemeinschaft: string | undefined) => {
    //     let personType = person_type !== undefined ? person_type : 0;
    //     let anteil = anteileigentuemer !== undefined && personType === 1 ? anteileigentuemer : '';
    //     let mitanteil = miteigentumsanteil !== undefined && personType === 1 ? miteigentumsanteil : '';
    //     let recht = artrechtsgemeinschaft !== undefined && personType === 1 ? artrechtsgemeinschaft : '';
    //
    //     setPersonType(personType);
    //     setAnteileigentuemer(anteil);
    //     setMiteigentumsanteil(mitanteil);
    //     setArtrechtsgemeinschaft(recht);
    //
    //     props.onChange(personType, anteil, mitanteil, recht);
    // }

    const onChange = (key: string, value: any) => {
        setMapping((prevState: any) => {
            let temp = {...prevState};

            switch (key) {
                case 'datum_soll':
                case 'datum_ist':
                    temp[key] = value ? value.toISOString() : '';
                    break;
                default:
                    temp[key] = value;
                    break;
            }


            props.onChange(temp);

            return temp;
        });
    };

    const lfdNrFields = () => {
        return <>
            <div className={'col-12'}>
                <label>{t('globalData:landRegister.abteilung1.col1')}</label>
                <Input edit={true} name={'lfdnr_eintragung'} value={mapping.lfdnr_eintragung} tabIndex={12}
                       onChange={(e: any) => {
                           onChange('lfdnr_eintragung', e.target.value);
                       }}/>
            </div>
            <div className={'col-12'}>
                <label>{t('globalData:landRegister.abteilung1.col3')}</label>
                <Input edit={true} name={'lfdnr_bestandsverzeichnis'} value={mapping.lfdnr_bestandsverzeichnis}
                       tabIndex={12}
                       onChange={(e: any) => {
                           onChange('lfdnr_bestandsverzeichnis', e.target.value);
                       }}/>
            </div>
            <div className={'col-12'}>
                <label>{t('globalData:landRegister.abteilung1.col4')}</label>
                <Input edit={true} name={'grundlage_eintragung'} value={mapping.grundlage_eintragung} tabIndex={12}
                       onChange={(e: any) => {
                           onChange('grundlage_eintragung', e.target.value);
                       }}/>
            </div>
        </>;
    };

    const showAutoComplete = () => {
        return props.mappingType !== MappingType.personCorrespondence && props.mappingType !== MappingType.parcelContract;
    };

    const showPersonType = () => {
        return props.mappingType !== MappingType.personCorrespondence && props.mappingType !== MappingType.parcelContract;
    };

    return (<div style={{width: '400px'}}>
        {props.name && <h5>{props.name}</h5>}
        {showAutoComplete() && props.autoCompleteComponent && props.autoCompleteComponent}
        {props.mappingType === MappingType.landRegisterParcel && <div>
            <div className={classNames('grid', props.className)}>
                <div className={'col-12'}>
                    <label>{t('globalData:parcels.bvnr')}</label>
                    <Input edit={true} name={'bvnr'} value={mapping.bvnr} type={'number'} tabIndex={11}
                           onChange={(e: any) => {
                               onChange('bvnr', e);
                           }}/>
                </div>
                <div className={'col-12'}>
                    <label>{t('globalData:parcels.gr_ha')}</label>
                    <Input edit={true} name={'gr_ha'} value={mapping.gr_ha} type={'number'} tabIndex={13}
                           onChange={(e: any) => {
                               onChange('gr_ha', e);
                           }}/>
                </div>
                <div className={'col-12'}>
                    <label>{t('globalData:parcels.gr_a')}</label>
                    <Input edit={true} name={'gr_a'} value={mapping.gr_a} type={'number'} tabIndex={12}
                           onChange={(e: any) => {
                               onChange('gr_a', e);
                           }}/>
                </div>
                <div className={'col-12'}>
                    <label>{t('globalData:parcels.gr_m2')}</label>
                    <Input edit={true} name={'gr_m2'} value={mapping.gr_m2} type={'number'} tabIndex={14}
                           onChange={(e: any) => {
                               onChange('gr_m2', e);
                           }}/>
                </div>
            </div>
        </div>}
        {props.mappingType !== MappingType.landRegisterParcel && showPersonType() &&
            <div className={classNames('grid', props.className)}>
                <div className={'col-12'}>
                    <label>{t('globalData:columns.personType')}</label>
                    <Input edit={true} name={'person_type_id'} value={mapping.person_type_id} type={'dropdown'}
                           tabIndex={11}
                           dropdownOptions={personTypes}
                        // disabled={props.mappingType === MappingType.personLandRegister}
                           onChange={(e: any) => {
                               onChange('person_type_id', e.value);
                           }}/>
                </div>
                {/*Eigentümer*/}
                {mapping.person_type_id === 1 && props.mappingType === MappingType.personLandRegister && <>
                    <div className={'col-12'}>
                        <label>{t('globalData:persons.anteileigentuemer')}</label>
                        <Input edit={true} name={'anteileigentuemer'} value={mapping.anteileigentuemer} tabIndex={12}
                               onChange={(e: any) => {
                                   onChange('anteileigentuemer', e.target.value);
                               }}/>
                        {showAnteilEigentuemerError &&
                            <small
                                className="p-error custom">{t('globalData:global.mapping.anteilEigentuemerFormatError')}</small>}
                    </div>
                    {lfdNrFields()}
                </>}
                {/*Eigentümergemeinschaft / WEG*/}
                {mapping.person_type_id === 6 && props.mappingType === MappingType.personLandRegister && <>
                    <div className={'col-12'}>
                        <label>{t('globalData:persons.anteileigentuemer')}</label>
                        <Input edit={true} name={'anteileigentuemer'} value={mapping.anteileigentuemer} tabIndex={12}
                               onChange={(e: any) => {
                                   onChange('anteileigentuemer', e.target.value);
                               }}/>
                        {showAnteilEigentuemerError &&
                            <small
                                className="p-error custom">{t('globalData:global.mapping.anteilEigentuemerFormatError')}</small>}
                    </div>
                    {lfdNrFields()}
                </>}
                {/*Erbengemeinschaft*/}
                {mapping.person_type_id === 7 && props.mappingType === MappingType.personLandRegister && <>
                    <div className={'col-12'}>
                        <label>{t('globalData:persons.eigentumsanteilLautGrundbuch')}</label>
                        <Input edit={true} name={'anteileigentuemer'} value={mapping.anteileigentuemer} tabIndex={12}
                               onChange={(e: any) => {
                                   onChange('anteileigentuemer', e.target.value);
                               }}/>
                        {showAnteilEigentuemerError &&
                            <small
                                className="p-error custom">{t('globalData:global.mapping.anteilEigentuemerFormatError')}</small>}
                    </div>
                    {lfdNrFields()}
                    {<div className={'col-12 grid-nogutter'}>
                        <MappingPersons projectId={props.projectId} activeMappings={props.activeMappings}
                                        persons={mapping.erbengemeinschaft}
                                        onChange={(persons: any[]) => {
                                            onChange('erbengemeinschaft', persons);
                                        }}/>
                    </div>}
                </>}
                {/*Eheleute mit Gütergemeinschaft*/}
                {mapping.person_type_id === 8 && props.mappingType === MappingType.personLandRegister && <>
                    <div className={'col-12'}>
                        <label>{t('globalData:persons.ehepartner')}</label>
                        <Input edit={true} name={'ehepartner'} value={mapping.ehepartner} tabIndex={13}
                               type={'dropdown'}
                               dropdownOptions={persons && persons.data && persons.data.map((item: Person) => {
                                   return {
                                       value: item.person_id, label: getPersonLabel(item)
                                   };
                               })}
                               onChange={(e: any) => {
                                   onChange('ehepartner', e.target.value);
                               }}/>
                    </div>
                    <div className={'col-12'}>
                        <label>{t('globalData:persons.anteileigentuemer')}</label>
                        <Input edit={true} name={'anteileigentuemer'} value={mapping.anteileigentuemer} tabIndex={12}
                               onChange={(e: any) => {
                                   onChange('anteileigentuemer', e.target.value);
                               }}/>
                        {showAnteilEigentuemerError &&
                            <small
                                className="p-error custom">{t('globalData:global.mapping.anteilEigentuemerFormatError')}</small>}
                    </div>
                    {lfdNrFields()}
                </>}
            </div>}
        {props.mappingType === MappingType.personCorrespondence &&
            <div className={classNames('grid', props.className)}>
                <div className={'col-12'}>
                    <label>{t('globalData:columns.korrespondenz_type_id')}</label>
                    <Input edit={true} name={'korrespondenz_type_id'} value={mapping.korrespondenz_type_id}
                           type={'dropdown'}
                           tabIndex={11}
                           disabled={props.edit}
                           dropdownOptions={correspondenceTypes}
                        // disabled={props.mappingType === MappingType.personLandRegister}
                           onChange={(e: any) => {
                               onChange('korrespondenz_type_id', e.value);
                           }}/>
                </div>
                <div className={'col-6'}>
                    <label>{t('globalData:columns.datum_soll')}</label>
                    <Input edit={true} name={'datum_soll'}
                           value={mapping.datum_soll ? new Date(mapping.datum_soll) : undefined}
                           type={'date'}
                           showTime={false}
                           tabIndex={11}
                        // disabled={props.mappingType === MappingType.personLandRegister}
                           onChange={(e: any) => {
                               onChange('datum_soll', e.value);
                           }}/>
                </div>
                <div className={'col-6'}>
                    <label>{t('globalData:columns.datum_ist')}</label>
                    <Input edit={true} name={'datum_ist'}
                           value={mapping.datum_ist ? new Date(mapping.datum_ist) : undefined}
                           type={'date'}
                           showTime={false}
                           tabIndex={11}
                        // disabled={props.mappingType === MappingType.personLandRegister}
                           onChange={(e: any) => {
                               onChange('datum_ist', e.value);
                           }}/>
                </div>
            </div>}
        {props.mappingType === MappingType.parcelContract &&
            <div className={classNames('grid', props.className)}>
                <div className={'col-12'}>
                    <label>{t('globalData:columns.vertrag_type_id')}</label>
                    <Input edit={true} name={'vertrag_type_id'} value={mapping.vertrag_type_id}
                           type={'dropdown'}
                           tabIndex={11}
                           disabled={props.edit}
                           dropdownOptions={contractTypes}
                        // disabled={props.mappingType === MappingType.personLandRegister}
                           onChange={(e: any) => {
                               onChange('vertrag_type_id', e.value);
                           }}/>
                </div>
                <div className={'col-6'}>
                    <label>{t('globalData:columns.datum_soll')}</label>
                    <Input edit={true} name={'datum_soll'}
                           value={mapping.datum_soll ? new Date(mapping.datum_soll) : undefined}
                           type={'date'}
                           showTime={false}
                           tabIndex={11}
                        // disabled={props.mappingType === MappingType.personLandRegister}
                           onChange={(e: any) => {
                               onChange('datum_soll', e.value);
                           }}/>
                </div>
                <div className={'col-6'}>
                    <label>{t('globalData:columns.datum_ist')}</label>
                    <Input edit={true} name={'datum_ist'}
                           value={mapping.datum_ist ? new Date(mapping.datum_ist) : undefined}
                           type={'date'}
                           showTime={false}
                           tabIndex={11}
                        // disabled={props.mappingType === MappingType.personLandRegister}
                           onChange={(e: any) => {
                               onChange('datum_ist', e.value);
                           }}/>
                </div>
                <div className={'col-6'}>
                    <label>{t('globalData:columns.flaeche_erwerb')}</label>
                    <Input edit={true} name={'flaeche_erwerb'}
                           value={mapping.flaeche_erwerb}
                           type={'number'}
                           showTime={false}
                           tabIndex={11}
                        // disabled={props.mappingType === MappingType.personLandRegister}
                           onChange={(e: any) => {
                               onChange('flaeche_erwerb', e);
                           }}/>
                </div>
                <div className={'col-6'}>
                    <label>{t('globalData:columns.flaeche_dinglich')}</label>
                    <Input edit={true} name={'flaeche_dinglich'}
                           value={mapping.flaeche_dinglich}
                           type={'number'}
                           showTime={false}
                           tabIndex={11}
                        // disabled={props.mappingType === MappingType.personLandRegister}
                           onChange={(e: any) => {
                               onChange('flaeche_dinglich', e);
                           }}/>
                </div>
                <div className={'col-6'}>
                    <label>{t('globalData:columns.flaeche_temp')}</label>
                    <Input edit={true} name={'flaeche_temp'}
                           value={mapping.flaeche_temp}
                           type={'number'}
                           showTime={false}
                           tabIndex={11}
                        // disabled={props.mappingType === MappingType.personLandRegister}
                           onChange={(e: any) => {
                               onChange('flaeche_temp', e);
                           }}/>
                </div>
            </div>}
    </div>);
};
